import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface ProductData {
	productName: string;
	containerType: string;
	tariffCode: string;
	unit: string;
	weightVolume: string;
	price: string;
}

interface ProductErrors {
	productName: string;
	weightVolume: string;
	price: string;
	tariffCode: string;
}

interface FormData {
	products: ProductData[];
	selectedCountries: any[];
}

interface FormErrors {
	products: ProductErrors[];
	selectedCountries: string;
}

interface CurrentPack {
	userSubscriptionId: string;
	productCount?: number;
	countryCount?: number;
}

export const usePriceComparatorForm = (currentPack?: CurrentPack | string) => {
	const { t } = useTranslation();

	// Determinar el número máximo de productos basado en el currentPack
	const getMaxProducts = () => {
		// Si currentPack es un string, usar 1 como valor por defecto
		if (typeof currentPack === 'string') return 1;

		// Si no hay currentPack, usar 1 como valor por defecto
		if (!currentPack) return 1;

		// Usar el productCount del currentPack, con 1 como valor por defecto
		return currentPack.productCount || 1;
	};

	// Determinar el número máximo de países basado en el currentPack
	const getMaxCountries = () => {
		// Si currentPack es un string, usar 1 como valor por defecto
		if (typeof currentPack === 'string') return 1;

		// Si no hay currentPack, usar 1 como valor por defecto
		if (!currentPack) return 1;

		// Usar el countryCount del currentPack, con 1 como valor por defecto
		return currentPack.countryCount || 1;
	};

	const [maxProducts, setMaxProducts] = useState(getMaxProducts());
	const [maxCountries, setMaxCountries] = useState(getMaxCountries());
	const [formData, setFormData] = useState<FormData>({
		products: Array(getMaxProducts())
			.fill(null)
			.map(() => ({
				productName: '',
				containerType: '',
				tariffCode: '',
				unit: 'kg',
				weightVolume: '',
				currency: 'EUR',
				price: '',
			})),
		selectedCountries: [],
	});

	const [errors, setErrors] = useState<FormErrors>({
		products: Array(getMaxProducts())
			.fill(null)
			.map(() => ({
				productName: '',
				weightVolume: '',
				price: '',
				tariffCode: '',
			})),
		selectedCountries: '',
	});

	// Efecto para manejar cambios en currentPack
	useEffect(() => {
		const newMaxProducts = getMaxProducts();
		const newMaxCountries = getMaxCountries();

		// Solo actualizar si el número de productos o países cambió
		if (newMaxProducts !== maxProducts || newMaxCountries !== maxCountries) {
			setMaxProducts(newMaxProducts);
			setMaxCountries(newMaxCountries);

			// Reinicializar el formulario con el nuevo número de productos
			setFormData({
				products: Array(newMaxProducts)
					.fill(null)
					.map(() => ({
						productName: '',
						containerType: '',
						tariffCode: '',
						unit: 'kg',
						weightVolume: '',
						price: '',
					})),
				selectedCountries: [],
			});

			setErrors({
				products: Array(newMaxProducts)
					.fill(null)
					.map(() => ({
						productName: '',
						weightVolume: '',
						price: '',
						tariffCode: '',
					})),
				selectedCountries: '',
			});
		}
	}, [currentPack, maxProducts, maxCountries]);

	// Función para validar el formulario
	const validateForm = (): boolean => {
		const newErrors: FormErrors = {
			products: formData.products.map(() => ({
				productName: '',
				weightVolume: '',
				price: '',
				tariffCode: '',
			})),
			selectedCountries: '',
		};

		let hasErrors = false;

		// Validar cada producto
		formData.products.forEach((product, index) => {
			if (!product.productName.trim()) {
				newErrors.products[index].productName = t('price_comparator.step_detail.product_name_error');
				hasErrors = true;
			}

			if (!product.weightVolume.trim()) {
				newErrors.products[index].weightVolume = t('price_comparator.step_detail.weight_volume_error');
				hasErrors = true;
			} else if (isNaN(Number(product.weightVolume)) || Number(product.weightVolume) <= 0) {
				newErrors.products[index].weightVolume = t('price_comparator.step_detail.weight_volume_error');
				hasErrors = true;
			}

			if (!product.price.trim()) {
				newErrors.products[index].price = t('price_comparator.step_detail.price_error');
				hasErrors = true;
			} else if (isNaN(Number(product.price)) || Number(product.price) <= 0) {
				newErrors.products[index].price = t('price_comparator.step_detail.price_error');
				hasErrors = true;
			}

			if (!product.tariffCode.trim()) {
				newErrors.products[index].tariffCode = t('price_comparator.step_detail.tarif_code_error');
				hasErrors = true;
			}
		});

		// Validar países seleccionados
		if (!formData.selectedCountries || formData.selectedCountries.length === 0) {
			newErrors.selectedCountries = t('price_comparator.step_detail.countries_error');
			hasErrors = true;
		} else if (formData.selectedCountries.length !== maxCountries) {
			newErrors.selectedCountries = t('price_comparator.step_detail.countries_count_error', { count: maxCountries });
			hasErrors = true;
		}

		setErrors(newErrors);
		return !hasErrors;
	};

	// Función para actualizar los datos del formulario
	const handleInputChange = (productIndex: number, field: keyof ProductData, value: any) => {
		setFormData((prev) => ({
			...prev,
			products: prev.products.map((product, index) =>
				index === productIndex ? { ...product, [field]: value } : product,
			),
		}));

		// Limpiar error del campo cuando el usuario empiece a escribir
		if (errors.products[productIndex]?.[field as keyof ProductErrors]) {
			setErrors((prev) => ({
				...prev,
				products: prev.products.map((productErrors, index) =>
					index === productIndex ? { ...productErrors, [field]: '' } : productErrors,
				),
			}));
		}
	};

	// Función para actualizar países seleccionados
	const handleCountriesChange = (value: any[]) => {
		setFormData((prev) => ({
			...prev,
			selectedCountries: value,
		}));

		if (errors.selectedCountries) {
			setErrors((prev) => ({
				...prev,
				selectedCountries: '',
			}));
		}
	};

	// Función para resetear el formulario
	const resetForm = () => {
		setFormData({
			products: Array(maxProducts)
				.fill(null)
				.map(() => ({
					productName: '',
					containerType: '',
					tariffCode: '',
					unit: 'kg',
					weightVolume: '',
					price: '',
				})),
			selectedCountries: [],
		});
		setErrors({
			products: Array(maxProducts)
				.fill(null)
				.map(() => ({
					productName: '',
					weightVolume: '',
					price: '',
					tariffCode: '',
				})),
			selectedCountries: '',
		});
	};

	// Función para verificar si el formulario está completo
	const isFormComplete = (): boolean => {
		return (
			formData.products.every(
				(product) =>
					product.productName.trim() &&
					product.weightVolume.trim() &&
					product.price.trim() &&
					product.tariffCode.trim(),
			) && formData.selectedCountries.length === maxCountries
		);
	};

	return {
		formData,
		errors,
		validateForm,
		handleInputChange,
		handleCountriesChange,
		resetForm,
		isFormComplete,
		maxProducts,
		maxCountries,
	};
};
